import { type Member } from "@features/api/memberApi";
import { isIapChannel } from "@hooks/memberPlansData";
import type { PurchaseGtmPlan } from "@utils/member/purchaseGtm";
import type { OpenPlanResumeDraftGoalChatParams } from "@hooks/plan/usePlanGoalChatOpen";

const STORAGE_KEY = "onboarding-first-checkout";
const MAX_AGE_MS = 2 * 60 * 60 * 1000;
export const ONBOARDING_CHECKOUT_QUERY = "onboardingCheckout";

/**
 * 续接记录只接受 web 渠道（EDG-552：原写死 STRIPE / PAYPAL，AIRWALLEX 回跳会被当脏数据丢弃，draft goal 无法恢复）。
 * 不限定在已知枚举内：后端新下发的跳转式渠道（带 display_name）也要能续接。
 * session_id 不进续接记录，复用 `checkoutSessionStash`（键 = 产品 id），避免同一会话两份存储。
 */
const isWebPayChannel = (value: unknown): boolean =>
  typeof value === "string" && value.trim() !== "" && !isIapChannel(value);

export type OnboardingCheckout = {
  id: string;
  ownerId: string;
  createdAt: number;
  returnPath: string;
  selectedPlanId: string;
  product: PurchaseGtmPlan;
  channel: string;
  analyticsSource: string;
  draft: OpenPlanResumeDraftGoalChatParams;
  phase: "checkout" | "pending";
  /**
   * 用户主动关闭了「待生效」付费墙的时间。记录仍保留（付款晚些生效时还要续接 draft goal），
   * 但之后回到页面不再自动弹付费墙 / 确认遮罩，只静默查一次会员态（beta 2026-10-14 Q8）。
   */
  dismissedAt?: number;
};

export function readOnboardingCheckout(
  ownerId: string,
): OnboardingCheckout | undefined {
  if (!ownerId || typeof window === "undefined") {
    return;
  }
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    if (!value) {
      return;
    }
    if (
      value.ownerId !== ownerId ||
      typeof value.id !== "string" ||
      !value.id ||
      typeof value.draft?.draftGoalId !== "string" ||
      !value.draft.draftGoalId ||
      typeof value.draft.summary !== "string" ||
      typeof value.product?.id !== "string" ||
      !value.product.id ||
      typeof value.selectedPlanId !== "string" ||
      !value.selectedPlanId ||
      typeof value.returnPath !== "string" ||
      !value.returnPath.startsWith("/") ||
      value.returnPath.startsWith("//") ||
      !["checkout", "pending"].includes(value.phase) ||
      !isWebPayChannel(value.channel) ||
      !Number.isFinite(value.createdAt) ||
      value.createdAt > Date.now() ||
      (value.dismissedAt !== undefined &&
        !Number.isFinite(value.dismissedAt)) ||
      Date.now() - value.createdAt > MAX_AGE_MS
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    return value;
  } catch {
    return;
  }
}

export function saveOnboardingCheckout(value: OnboardingCheckout): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function consumeOnboardingCheckout(
  ownerId: string,
  id: string,
): OnboardingCheckout | undefined {
  const pending = readOnboardingCheckout(ownerId);
  if (!pending || pending.id !== id) {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEY);
  return pending;
}

export function hasEffectiveMembership(member: Member | undefined): boolean {
  return (
    member?.status === "ACTIVE" &&
    !!member.product?.level &&
    member.product.level !== "FREE"
  );
}

export async function pollOnboardingMembership(
  refresh: () => Promise<{ data?: Member }>,
  wait: () => Promise<void> = () =>
    new Promise((resolve) => setTimeout(resolve, 1000)),
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  for (let attempt = 0; attempt < 5; attempt++) {
    if (!isCurrent()) {
      return false;
    }
    try {
      const result = await refresh();
      if (!isCurrent()) {
        return false;
      }
      if (hasEffectiveMembership(result.data)) {
        return true;
      }
    } catch {
      /* Refresh remains retryable. */
    }
    if (!isCurrent()) {
      return false;
    }
    if (attempt < 4) {
      await wait();
    }
  }
  return false;
}
